import axios from 'shared/tools/axiosMultisite';
import { storageFactory } from 'shared/tools/localStorageFactory';
import { impressionListSources, sessionStorageKeys } from 'shared/settings/constants';
import createQueue from 'shared/tools/basketPromiseQueue';

const queue = createQueue();

const storage = storageFactory(window.sessionStorage);

export default {
    state: () => ({
        myProducts: [],
		editProduct: null,
		addToBasketRequestVerified: false,
		additionalProductsToAddToBasket: [],
		myNonRegularProductsData: {},
    }),
    getters: {
        getMyProducts: (state) => {
            return state.myProducts;
        },
		getEditProduct: (state) => {
			return state.editProduct;
		},
		getAddToBasketRequestVerified: (state) => {
			return state.addToBasketRequestVerified;
		},
		getAdditionalProductsToAddToBasket: (state) => {
			return state.additionalProductsToAddToBasket;
		},
		getMyNonRegularProductsData: (state) => {
			return state.myNonRegularProductsData;
		},
    },
    mutations: {
		setMyProducts: (state, products) => {
			state.myProducts = products;
		},
		setEditProduct: (state, product) => {
			state.editProduct = product;
		},
		setAddToBasketRequestVerified: (state, isVerified) => {
			state.addToBasketRequestVerified = isVerified;
		},
		addAdditionalProductToAddToBasket: (state, code) => {
			if (!state.additionalProductsToAddToBasket.includes(code)) {
				state.additionalProductsToAddToBasket.push(code);
			}
		},
		removeAddtionalProductToAddToBasket: (state, code) => {
			if (state.additionalProductsToAddToBasket.includes(code)) {
				state.additionalProductsToAddToBasket = state.additionalProductsToAddToBasket.filter(c => c != code);
			}
		},
		setMyProductsUsageAndPrice: (state, calculatedProducts) => {
			calculatedProducts.forEach((calulatedProduct) => {
				let index = state.myProducts.findIndex((myProduct) => {
					return myProduct.code === calulatedProduct.code;
				})
				if (index >= 0) {
					let myProduct = {...state.myProducts[index] };
					myProduct.numberOfPacks = calulatedProduct.quantity;
					myProduct.numberOfItems = calulatedProduct.quantityInItems;
					myProduct.formattedPrice = calulatedProduct.formattedPrice;
					state.myProducts.splice(index, 1, myProduct);
				}
			})
		},
		setMyNonRegularProductsData: (state, data) => {
			storage.setItem(sessionStorageKeys.NON_REGULAR_PRODUCTS_KEY, JSON.stringify({
				timestamp: Date.now(),
				myNonRegularProductsData: data,
			}));
			state.myNonRegularProductsData = data;
		},
		clearMyNonRegularProductsData: (state) => {
			storage.removeItem(sessionStorageKeys.NON_REGULAR_PRODUCTS_KEY);
			state.myNonRegularProductsData = {};
		},
    },
    actions: {
		fetchMyProducts: ( {commit, state}, culture ) => {
			return axios.get(`/api/consumeraccount/consumerproducts?language=${culture}`)
				.then(response => {
					if (response.data) {
						commit('setMyProducts', response.data.products);
					}
				})
				.catch(error => {
					commit('setMyProducts', []);
				})
		},
		fetchEditProduct: ({ commit, state }, payload) => {
			return axios.get(`/api/consumeraccount/getconsumerproductbycode?productcode=${payload.code}&language=${payload.culture}`)
				.then(response => {
					if (response.data.products) {
						commit('setEditProduct', response.data.products[0]);
					}
				})
				.catch(error => {
					commit('setEditProduct', null);
				})
		},
		verifyAddMyProducts: ({ commit, rootGetters }, periodInDays) => {
			let payload = {
				periodInDays,
				language: rootGetters['getCulture'],
			};
			return queue.add(() => {
				return axios.post('/api/consumeraccount/consumerverifyaddmyproducts', payload)
				.then(response => {
					commit('setAddToBasketRequestVerified', response.data.isValidForAddingMyProducts);
				});
			});
		},
		addMyProductsToBasket: ({ commit, rootGetters, state }, data) => {
			return queue.add(() => {
				return axios.post('/api/consumeraccount/consumeraddmyproducts', {
					periodInDays: data.periodInDays,
					language: rootGetters['getCulture'],
					additionalItems: state.additionalProductsToAddToBasket,
					trackingParameters: data.trackingParameters,
				})
				.then(response => {
					commit('setAddToBasketRequestVerified', false);
				})
			});
		},
		calculateMyProducts: ({ commit, rootGetters }, periodInDays) => {
			return axios.post('/api/consumeraccount/consumercalculatemyproducts', {
				periodInDays: periodInDays,
				language: rootGetters['getCulture'],
			})
			.then(response => {
				commit('setMyProductsUsageAndPrice', response.data.items)
			})
		},
		fetchMyNonRegularProducts: ({ commit, rootGetters }) => {
			const myRegularProductsJson = storage.getItem(sessionStorageKeys.NON_REGULAR_PRODUCTS_KEY);
			if (myRegularProductsJson) {
				const myRegularProductsContainer = JSON.parse(myRegularProductsJson);
				// if the timestamp is less than 15 minutes old use the products that were fetched from localStorage
				if (myRegularProductsContainer.timestamp && myRegularProductsContainer.timestamp + 1000*60*15 > Date.now()) {
					commit('setMyNonRegularProductsData', myRegularProductsContainer.myNonRegularProductsData);
					return Promise.resolve();
				}
			}
			return axios.get(`/api/consumeraccount/get-non-regular-my-products?language=${rootGetters['getCulture']}`)
				.then((response) => {
					commit('setMyNonRegularProductsData', response.data);
					return response.data;
				})
		},
		addNonRegularProductsToBasket: async ({ rootGetters }, products) => {
			let itemsPayload = products.map(
				product => ({
					code: product.code,
					quantity: product.quantity,
				})
			);
			return queue.add(() => {
				return axios.post('/api/global-basket/add-multiple-items', {
					itemsToAdd: itemsPayload,
					language: rootGetters['getCulture'],
					listSource: impressionListSources.NON_DISPOSABLE_BASKET_SECTION,
				})
				.then(response => {
					return response.data;
				});
			});
		},
		addReorderedProductsToBasket: async ({ rootGetters }, products) => {
			let itemsPayload = products.map(
				product => ({
					code: product.code,
					quantity: product.catalogContent.quantity,
				})
			);
			return queue.add(() => {
				return axios.post('/api/global-basket/add-multiple-items', {
					itemsToAdd: itemsPayload,
					language: rootGetters['getCulture'],
					listSource: impressionListSources.MY_PRODUCTS,
				})
				.then(response => {
					return response.data;
				})
				.catch((error) => {
					return error.response.data
				});
			});
		},
    },
}
